import React__default, { forwardRef, createContext, useContext, useCallback, createElement, useState, useEffect } from 'react';
import styled, { useTheme, createGlobalStyle, ThemeProvider } from 'styled-components';
import { compose, color as color$1, system, typography as typography$1, space as space$1, layout, flexbox, grid, background, border, position, shadow, variant } from 'styled-system';
import systemCss from '@styled-system/css';
import Rating from '@material-ui/lab/Rating';
import SvgIcon from '@material-ui/core/SvgIcon';
import ReachAlert from '@reach/alert';
import { AnimatePresence, motion } from 'framer-motion';
import { RadioGroup } from '@headlessui/react';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var BASE_FONT = 18;
/**
 * Converts pixels to their REM equivalent at a given abse
 * @param px The size in Pixels to convert
 * @param base The base to scale the REM relative to
 * @returns {string} an REM string
 */

function pxToRem(px) {
  return px / BASE_FONT + "rem";
}

var crunchberry = {
  xlight: '#FDECF4',
  light: '#E5A5C5',
  base: '#E30C79',
  medium: '#F50B7C',
  dark: '#CC0D71'
};
var grape = {
  xlight: '#FAF5FF',
  base: '#A381C5',
  medium: '#8D65B5',
  dark: '#59377B'
};
var teal = {
  dark: '#006273',
  medium: '#008196',
  base: '#0095AF',
  light: '#52CEE6',
  xlight: '#EEFAFC'
};
var gray = {
  '100': '#F7F7F7',
  '200': '#E3E5E6',
  '300': '#C6CACF',
  '400': '#8C95A1',
  '500': '#6A7685',
  '600': '#222A33'
};
var mint = {
  base: '#28D0B2',
  dark: '#187D6A',
  light: '#E9FAF7'
};
var peach = {
  base: '#FFA400',
  dark: '#9C6400',
  light: '#FFF6E5'
};
var cherry = {
  base: '#CE2424',
  dark: '#901919',
  light: '#FAE9E9'
};
var blueberry = {
  base: '#0072B0',
  light: '#F2F8FB'
};
var eggplant = {
  base: '#682E9A',
  light: '#F7F5FA'
};
var broccoli = {
  base: '#107A70',
  light: '#E7F2F1'
};
var egg = '#FFFFFF';
var karl = '#EDF4F9';
var black = '#000000';
var palette = {
  crunchberry: crunchberry,
  grape: grape,
  gray: gray,
  teal: teal,
  mint: mint,
  peach: peach,
  cherry: cherry,
  blueberry: blueberry,
  eggplant: eggplant,
  broccoli: broccoli,
  egg: egg,
  white: egg,
  black: black
};
var colors = {
  crunchberry: crunchberry.base,
  'crunchberry-dark': crunchberry.dark,
  'crunchberry-medium': crunchberry.medium,
  'crunchberry-light': crunchberry.light,
  'crunchberry-xlight': crunchberry.xlight,
  teal: teal.base,
  'teal-dark': teal.dark,
  'teal-medium': teal.medium,
  'teal-light': teal.light,
  'teal-xlight': teal.xlight,
  grape: grape.base,
  'grape-dark': grape.dark,
  'grape-medium': grape.medium,
  'grape-xlight': grape.xlight,
  'gray-100': gray['100'],
  'gray-200': gray['200'],
  'gray-300': gray['300'],
  'gray-400': gray['400'],
  'gray-500': gray['500'],
  'gray-600': gray['600'],
  mint: mint.base,
  'mint-dark': mint.dark,
  'mint-light': mint.light,
  peach: peach.base,
  'peach-dark': peach.dark,
  'peach-light': peach.light,
  cherry: cherry.base,
  'cherry-dark': cherry.dark,
  'cherry-light': cherry.light,
  blueberry: blueberry.base,
  'blueberry-light': blueberry.light,
  eggplant: eggplant.base,
  'eggplant-light': eggplant.light,
  broccoli: broccoli.base,
  'broccoli-light': broccoli.light,
  egg: egg,
  white: egg,
  black: black,
  karl: karl
};

var colors$1 = {
  __proto__: null,
  crunchberry: crunchberry,
  grape: grape,
  teal: teal,
  gray: gray,
  mint: mint,
  peach: peach,
  cherry: cherry,
  blueberry: blueberry,
  eggplant: eggplant,
  broccoli: broccoli,
  egg: egg,
  karl: karl,
  black: black,
  palette: palette,
  'default': colors
};

var BASE_RADIUS = 8;
var radii = {
  none: /*#__PURE__*/pxToRem(0),
  xs: /*#__PURE__*/pxToRem(2),
  sm: /*#__PURE__*/pxToRem(4),
  base: /*#__PURE__*/pxToRem(BASE_RADIUS),
  lg: /*#__PURE__*/pxToRem(12),
  xl: /*#__PURE__*/pxToRem(16),
  full: /*#__PURE__*/pxToRem(1000)
};
var shadows = {
  none: '0 0 0 rgba(0,0,0,0)',
  xs: '0px 1px 4px rgba(0,0,0,.16)',
  sm: '0px 2px 8px rgba(0,0,0,.16)',
  md: '0px 4px 16px rgba(0,0,0,.16)',
  lg: '0px 8px 24px rgba(0,0,0,.16)',
  focus: "0 0 0 var(--focus-offset) var(--focusable-offset-color), 0 0 0 calc(var(--focus-width) + var(--focus-offset)) " + gray[600]
};

var fonts = {
  sans: 'Circular Pro, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
};
var fontWeights = {
  bold: 700,
  medium: 500,
  regular: 400
};
var fontSizes = {
  xs: /*#__PURE__*/pxToRem(12),
  sm: /*#__PURE__*/pxToRem(14),
  base: /*#__PURE__*/pxToRem(16),
  lg: /*#__PURE__*/pxToRem(20),
  xl: /*#__PURE__*/pxToRem(24),
  '2xl': /*#__PURE__*/pxToRem(28),
  '3xl': /*#__PURE__*/pxToRem(32)
};
var lineHeights = {
  xs: /*#__PURE__*/pxToRem(16),
  sm: /*#__PURE__*/pxToRem(20),
  base: /*#__PURE__*/pxToRem(24),
  lg: /*#__PURE__*/pxToRem(28),
  xl: /*#__PURE__*/pxToRem(32),
  '2xl': /*#__PURE__*/pxToRem(36),
  '3xl': /*#__PURE__*/pxToRem(40)
};

var preSpace = {
  0: 0,
  0.5: /*#__PURE__*/pxToRem(4),
  1: /*#__PURE__*/pxToRem(8),
  1.5: /*#__PURE__*/pxToRem(12),
  2: /*#__PURE__*/pxToRem(16),
  2.5: /*#__PURE__*/pxToRem(20),
  3: /*#__PURE__*/pxToRem(24),
  4: /*#__PURE__*/pxToRem(32),
  5: /*#__PURE__*/pxToRem(40),
  6: /*#__PURE__*/pxToRem(48),
  7: /*#__PURE__*/pxToRem(56),
  8: /*#__PURE__*/pxToRem(64),
  9: /*#__PURE__*/pxToRem(72),
  10: /*#__PURE__*/pxToRem(80),
  11: /*#__PURE__*/pxToRem(88),
  12: /*#__PURE__*/pxToRem(96),
  13: /*#__PURE__*/pxToRem(104),
  14: /*#__PURE__*/pxToRem(112),
  15: /*#__PURE__*/pxToRem(120)
};
var space = preSpace;

var partialBreakpoints = ['600px', '900px', '1200px'];
partialBreakpoints._ = 0;
partialBreakpoints.sm = partialBreakpoints[0];
partialBreakpoints.md = partialBreakpoints[1];
partialBreakpoints.lg = partialBreakpoints[2];
var breakpoints = partialBreakpoints;

/**
 * This is the the them object conforming to the SystemUI Theme Spec:
 * (see https://system-ui.com/theme)
 *
 * All other themes should be derived from this theme or its constituent values
 */

var SolvTheme = {
  colors: colors,
  fonts: fonts,
  fontWeights: fontWeights,
  fontSizes: fontSizes,
  lineHeights: lineHeights,
  space: space,
  radii: radii,
  shadows: shadows,
  breakpoints: breakpoints
};

/**
 * Takes a set of system styles and resolve them to a css object
 * @param styles The styles to convert
 * @returns a css object
 */

function style(styles) {
  var _ref, _styles$textColor;

  var transformed = _extends({}, styles, {
    // @ts-expect-error
    color: (_ref = (_styles$textColor = styles.textColor) != null ? _styles$textColor : styles.color) != null ? _ref : undefined
  }); // @ts-expect-error


  delete transformed.textColor;
  return systemCss(transformed)({
    theme: SolvTheme
  });
}

function taggedTemplateNoop(strings) {
  for (var _len = arguments.length, keys = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    keys[_key - 1] = arguments[_key];
  }

  var lastIndex = strings.length - 1;
  return strings.slice(0, lastIndex).reduce(function (p, s, i) {
    return p + s + keys[i];
  }, '') + strings[lastIndex];
}
/**
 * This is a noop string tag. It takes a string and returns the same string.
 * It exists to let you have nice syntax highlighting in your editor.
 *
 * @example styled.div(css`color: blue;`)
 */


var css = taggedTemplateNoop;

/**
 * The updated styled system integration with `textColor` instead of `color`
 */

var color = /*#__PURE__*/compose(color$1, /*#__PURE__*/system({
  // Alias color as textColor
  textColor: {
    property: 'color',
    scale: 'colors'
  }
}));

/** Injects the SX prop from `props` */

var sx = function sx(props) {
  var _props$sx;

  return style((_props$sx = props.sx) != null ? _props$sx : {});
};
/**
 * This value can be passed as an argument to styled-components `styled` function to add all of
 * the props we want.
 */

var solvSystem = /*#__PURE__*/compose(color, typography$1, space$1, layout, flexbox, grid, background, border, position, shadow);

var StyledBox = /*#__PURE__*/styled.div.withConfig({
  displayName: "Box__StyledBox",
  componentId: "sc-1gutema-0"
})(solvSystem, sx);
/**
 * Box is the lowest-level component provided by Jigsaw. It is a wrapper around a
 * display-block div with all of the Jigsaw system props.
 *
 * The base `Box` component is the workhorse of the design system. It has props
 * for nearly all css and design-system related fields necessary, and can replace
 * most instances of `div`. It's support for the `as` prop allows for better
 * semantic HTML (though it does not update the props of the `Box` to reflect the
 * new Element -- though this is possible it **really** slows down Typescript still.)
 *
 * Only a small selection of props are included in the storybook. See the `System Props` page
 * for more details.
 */

var Box = /*#__PURE__*/forwardRef(function (props, ref) {
  return React__default.createElement(StyledBox, Object.assign({}, props, {
    ref: ref
  }));
});

var _excluded = ["row", "className", "children"];
var VStack = /*#__PURE__*/styled.div.withConfig({
  displayName: "Stack__VStack",
  componentId: "sc-1tehqgc-0"
})({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch'
}, /*#__PURE__*/system({
  space: {
    scale: 'space',
    property: '&& > *:not(:last-child)',
    transform: function transform(value, scale) {
      return {
        marginBottom: scale == null ? void 0 : scale[value]
      };
    }
  }
}), solvSystem, sx);
var HStack = /*#__PURE__*/styled.div.withConfig({
  displayName: "Stack__HStack",
  componentId: "sc-1tehqgc-1"
})({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'flex-start'
}, function (props) {
  var _props$sx;

  return style((_props$sx = props.sx) != null ? _props$sx : {});
}, /*#__PURE__*/system({
  space: {
    scale: 'space',
    property: '&& > *:not(:last-child)',
    transform: function transform(value, scale) {
      return {
        marginRight: scale == null ? void 0 : scale[value]
      };
    }
  }
}), solvSystem, sx);
/**
 * The stack is a flex-column by default container that places space between each item within.
 */

var Stack = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var row = _ref.row,
      className = _ref.className,
      children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  if (row) return React__default.createElement(HStack, Object.assign({
    className: className
  }, props, {
    ref: ref
  }), children);
  return React__default.createElement(VStack, Object.assign({
    className: className
  }, props, {
    ref: ref
  }), children);
});

var typography = {
  h1: {
    fontSize: ['2xl', '3xl'],
    lineHeight: ['2xl', '3xl'],
    fontWeight: 'bold',
    fontFamily: 'sans'
  },
  h2: {
    fontSize: 'xl',
    lineHeight: 'xl',
    fontWeight: 'bold',
    fontFamily: 'sans'
  },
  h3: {
    fontSize: 'lg',
    lineHeight: 'lg',
    fontWeight: 'bold',
    fontFamily: 'sans'
  },
  h4: {
    fontSize: 'base',
    lineHeight: 'base',
    fontWeight: 'bold',
    fontFamily: 'sans'
  },
  h5: {
    fontSize: 'sm',
    lineHeight: 'sm',
    fontWeight: 'bold',
    fontFamily: 'sans'
  },
  subtitle: {
    fontSize: 'base',
    lineHeight: 'base',
    fontWeight: 'medium',
    fontFamily: 'sans'
  },
  subtitle2: {
    fontSize: 'sm',
    lineHeight: 'sm',
    fontWeight: 'medium',
    fontFamily: 'sans'
  },
  button: {
    fontSize: 'base',
    lineHeight: 'base',
    fontWeight: 'medium',
    fontFamily: 'sans'
  },
  body: {
    fontSize: 'base',
    lineHeight: 'base',
    fontWeight: 'regular',
    fontFamily: 'sans'
  },
  body2: {
    fontSize: 'sm',
    lineHeight: 'sm',
    fontWeight: 'regular',
    fontFamily: 'sans'
  },
  caption: {
    fontSize: 'xs',
    lineHeight: 'xs',
    fontWeight: 'regular',
    fontFamily: 'sans'
  }
};

var _excluded$1 = ["as", "muted", "variant"];
var componentDefaults = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5'
};
/**
 * The Text component is the base text of the app, supporing styles from the design system
 */

var StyledText = /*#__PURE__*/styled('p').withConfig({
  displayName: "Text__StyledText",
  componentId: "sc-6qawcz-0"
})(function (_ref) {
  var $muted = _ref.$muted;
  return style({
    margin: 0,
    textColor: $muted ? 'gray-500' : 'gray-600'
  });
}, /*#__PURE__*/variant({
  variants: typography
}), solvSystem);

var Text = function Text(_ref2) {
  var _ref3;

  var as = _ref2.as,
      muted = _ref2.muted,
      _ref2$variant = _ref2.variant,
      variant = _ref2$variant === void 0 ? 'body' : _ref2$variant,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded$1);

  var componentType = (_ref3 = as != null ? as : componentDefaults[variant]) != null ? _ref3 : 'p';
  return React__default.createElement(StyledText, Object.assign({
    as: componentType,
    "$muted": muted,
    variant: variant
  }, props));
};

var Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "Spinner__Root",
  componentId: "sc-mvrw8m-0"
})(["display:inline-block;position:relative;width:calc(", " * 1.25);height:calc(", " * 1.25);& div{box-sizing:border-box;display:block;position:absolute;width:", ";height:", ";margin:3px;border:3px solid ", ";border-radius:50%;animation:lds-ring 1.2s cubic-bezier(0.5,0,0.5,1) infinite;border-color:", " transparent transparent transparent;}& div:nth-child(1){animation-delay:-0.45s;}& div:nth-child(2){animation-delay:-0.3s;}& div:nth-child(3){animation-delay:-0.15s;}@keyframes lds-ring{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}"], function (_ref) {
  var size = _ref.size;
  return size;
}, function (_ref2) {
  var size = _ref2.size;
  return size;
}, function (_ref3) {
  var size = _ref3.size;
  return size;
}, function (_ref4) {
  var size = _ref4.size;
  return size;
}, function (_ref5) {
  var color = _ref5.color;
  return color;
}, function (_ref6) {
  var color = _ref6.color;
  return color;
});

var Spinner = function Spinner(_ref7) {
  var _theme$colors$color;

  var _ref7$color = _ref7.color,
      color = _ref7$color === void 0 ? 'gray-200' : _ref7$color,
      _ref7$size = _ref7.size,
      size = _ref7$size === void 0 ? '3xl' : _ref7$size;
  var theme = useTheme();
  var cssColor = (_theme$colors$color = theme.colors[color]) != null ? _theme$colors$color : color;
  var cssSize = theme.fontSizes[size];
  return React__default.createElement(Root, {
    color: cssColor,
    size: cssSize
  }, React__default.createElement("div", null), React__default.createElement("div", null), React__default.createElement("div", null), React__default.createElement("div", null));
};

var tones = {
  primary: {
    '--button-base': palette.crunchberry.base,
    '--button-dark': palette.crunchberry.dark,
    '--button-light': palette.crunchberry.medium,
    '--button-xlight': palette.crunchberry.xlight
  },
  error: {
    '--button-base': palette.cherry.base,
    '--button-dark': palette.cherry.base,
    '--button-light': palette.cherry.dark,
    '--button-xlight': palette.cherry.light
  },
  success: {
    '--button-base': palette.mint.base,
    '--button-dark': palette.mint.base,
    '--button-light': palette.mint.dark,
    '--button-xlight': palette.mint.light
  },
  warning: {
    '--button-base': palette.peach.base,
    '--button-dark': palette.peach.base,
    '--button-light': palette.peach.dark,
    '--button-xlight': palette.peach.light
  }
};
var buttons = {
  contained: {
    background: 'var(--button-base)',
    color: 'white',
    border: 'solid 1px var(--button-base)',
    ':visited': {
      color: 'white'
    },
    ':hover': {
      background: 'var(--button-light)',
      border: 'solid 1px var(--button-light)',
      color: 'white'
    },
    ':active': {
      background: 'var(--button-dark)',
      border: 'solid 1px var(--button-dark)',
      color: 'white'
    },
    ':disabled': {
      pointerEvents: 'none',
      bg: 'gray-200',
      color: 'gray-300',
      borderColor: 'gray-200'
    }
  },
  outlined: {
    background: 'transparent',
    color: 'var(--button-base)',
    border: 'solid 1px var(--button-base)',
    ':visited': {
      color: 'var(--button-base)'
    },
    ':hover': {
      background: 'var(--button-xlight)',
      color: 'var(--button-base)'
    },
    ':active': {
      background: 'transparent',
      color: 'var(--button-base)'
    },
    ':disabled': {
      bg: 'white',
      borderColor: 'gray-300',
      color: 'gray-300'
    }
  },
  text: /*#__PURE__*/_extends({}, typography.body, {
    background: 'transparent',
    color: 'var(--button-base)',
    border: 'solid 1px transparent',
    ':visited': {
      color: 'var(--button-base)'
    },
    ':hover': {
      background: 'var(--button-xlight)',
      color: 'var(--button-base)'
    },
    ':active': {
      background: 'transparent',
      color: 'var(--button-base)'
    },
    ':disabled': {
      bg: 'white',
      borderColor: 'gray-300',
      color: 'gray-300'
    }
  })
};
var buttonSizes = {
  large: {
    px: 3,
    py: 2
  },
  medium: {
    px: 2.5,
    py: 1.5
  },
  small: {
    px: 2,
    py: 1
  }
};

var GlobalStyles = /*#__PURE__*/createGlobalStyle(["body{--focusable-offset-color:white;--focus-offset:2px;--focus-width:2px;}.focusable{transition:box-shadow .15s;box-shadow:unset;}.focusable:focus{outline:none;box-shadow:0 0 0 var(--focus-offset) var(--focusable-offset-color),0 0 0 calc(var(--focus-width) + var(--focus-offset)) ", ";}.focusable:focus-visible{outline:none;box-shadow:0 0 0 var(--focus-offset) var(--focusable-offset-color),0 0 0 calc(var(--focus-width) + var(--focus-offset)) ", ";}.focusable:focus:not(:focus-visible){outline:none;box-shadow:none;}"], gray[600], gray[600]);

var SolvThemeProvider = function SolvThemeProvider(_ref) {
  var children = _ref.children;
  return React__default.createElement(ThemeProvider, {
    theme: SolvTheme
  }, children);
};

/**
 * A context containing  settings for Jigsaw
 */

var JigsawContext = /*#__PURE__*/createContext({
  history: {
    push: function push(path) {
      return console.log("[MOCK] Navigating to " + path);
    },
    goBack: function goBack() {
      return console.log("[MOCK] Going back");
    }
  }
});
var JigsawProvider = function JigsawProvider(_ref) {
  var children = _ref.children,
      settings = _ref.settings;
  if (!settings) throw new Error('A JigsawSettings object must be supplied to the JigsawProvider');
  return React__default.createElement(JigsawContext.Provider, {
    value: settings
  }, React__default.createElement(SolvThemeProvider, null, children, React__default.createElement(GlobalStyles, null)));
};
function useJigsaw() {
  var context = useContext(JigsawContext);

  if (!context) {
    throw new Error('A JigsawProivder must be present in the component tree');
  }

  return context;
}

var _excluded$2 = ["children", "disabled", "loading", "onClick", "startIcon", "endIcon", "accessory", "external", "openInNewWindow", "href", "goBack", "disableDefaultFocusStyles", "showAccessory", "contentProps"];
var ResetButton = /*#__PURE__*/styled.button.withConfig({
  displayName: "BaseClickable__ResetButton",
  componentId: "sc-42nmaq-0"
})(["cursor:pointer;border:none;background:none;padding:0;margin:0;text-align:left;appearance:none;display:", ";align-items:center;position:relative;border-radius:", ";&.disabled{cursor:auto;pointer-events:none;}", " ", ""], function (props) {
  return props.inline ? 'inline-flex' : 'flex';
}, SolvTheme.radii.base, solvSystem, sx);
var ResetLink = /*#__PURE__*/styled.a.withConfig({
  displayName: "BaseClickable__ResetLink",
  componentId: "sc-42nmaq-1"
})(["text-decoration:none;padding:0;margin:0;text-align:left;appearance:none;display:", ";align-items:center;position:relative;border-radius:", ";color:unset;&:hover,&:active,&:visited{color:unset;}&.disabled{cursor:auto;pointer-events:none;}", " ", ""], function (props) {
  return props.inline ? 'inline-flex' : 'flex';
}, SolvTheme.radii.base, solvSystem, sx);

var BaseClickable = function BaseClickable(_ref, ref) {
  var _restProps$className;

  var children = _ref.children,
      disabled = _ref.disabled,
      loading = _ref.loading,
      onClick = _ref.onClick,
      startIcon = _ref.startIcon,
      endIcon = _ref.endIcon,
      accessory = _ref.accessory,
      external = _ref.external,
      openInNewWindow = _ref.openInNewWindow,
      href = _ref.href,
      goBack = _ref.goBack,
      disableDefaultFocusStyles = _ref.disableDefaultFocusStyles,
      _ref$showAccessory = _ref.showAccessory,
      showAccessory = _ref$showAccessory === void 0 ? false : _ref$showAccessory,
      _ref$contentProps = _ref.contentProps,
      contentProps = _ref$contentProps === void 0 ? {} : _ref$contentProps,
      restProps = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var _useJigsaw = useJigsaw(),
      history = _useJigsaw.history; // Intercept click to handle disabled and loading state


  var handleClick = useCallback(function (event) {
    if (disabled || loading) {
      event.preventDefault();
    } else if (goBack) {
      history.goBack();
    } else if (href !== undefined) {
      history.push(href);
    } else if (onClick) {
      onClick(event);
    }
  }, [disabled, goBack, history, href, loading, onClick]);
  var props = {};
  var classname = (disableDefaultFocusStyles ? '' : 'focusable ') + (disabled ? ' disabled ' : '') + ((_restProps$className = restProps.className) != null ? _restProps$className : '');

  if (href) {
    if (openInNewWindow) {
      props.target = '__BLANK'; // Avoid tab hijacking or potential exposure of `window.opener`

      if (props.rel === undefined) {
        props.rel = 'noopener';
      }
    }

    if (!external) {
      props.onClick = function (e) {
        e.preventDefault();
        handleClick(e);
      };
    }

    return React__default.createElement(ResetLink, Object.assign({
      ref: ref,
      href: href
    }, props, restProps, {
      "aria-busy": loading,
      className: classname
    }), React__default.createElement(Stack, {
      space: 1,
      row: true,
      alignItems: "center",
      style: {
        display: 'contents',
        visibility: loading ? 'hidden' : 'visible'
      }
    }, startIcon && startIcon, React__default.createElement(Box, Object.assign({
      as: "span",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      sx: {
        pointerEvents: 'none'
      }
    }, contentProps), children), endIcon && endIcon), showAccessory && accessory);
  }

  return React__default.createElement(ResetButton, Object.assign({
    ref: ref,
    onClick: handleClick,
    disabled: disabled
  }, props, restProps, {
    className: classname
  }), React__default.createElement(Stack, {
    space: 1,
    row: true,
    alignItems: "center",
    style: {
      display: 'contents',
      visibility: loading ? 'hidden' : 'visible'
    }
  }, startIcon && startIcon, React__default.createElement(Box, Object.assign({
    as: "span",
    justifyContent: "center",
    display: "flex",
    alignItems: "center",
    sx: {
      pointerEvents: 'none'
    }
  }, contentProps), children), endIcon && endIcon), showAccessory && accessory);
};

var BaseClickable$1 = /*#__PURE__*/React__default.forwardRef(BaseClickable);

var _excluded$3 = ["children", "loading", "fullWidth", "startIcon", "endIcon", "tone", "variant", "size"];
var StyledButton = /*#__PURE__*/styled(BaseClickable$1).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-fwbsjg-0"
})({
  transition: 'background 75ms, box-shadow .15s, transform 25ms'
}, /*#__PURE__*/style(typography.button), /*#__PURE__*/variant({
  variants: buttons
}), /*#__PURE__*/variant({
  prop: '$size',
  variants: buttonSizes
}), /*#__PURE__*/variant({
  prop: '$tone',
  variants: tones
}), sx, solvSystem);

var Button = function Button(_ref) {
  var children = _ref.children,
      loading = _ref.loading,
      fullWidth = _ref.fullWidth,
      startIcon = _ref.startIcon,
      endIcon = _ref.endIcon,
      _ref$tone = _ref.tone,
      tone = _ref$tone === void 0 ? 'primary' : _ref$tone,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'contained' : _ref$variant,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$3);

  return React__default.createElement(StyledButton, Object.assign({
    variant: variant,
    "$size": size,
    borderRadius: "base",
    justifyContent: "center",
    startIcon: startIcon,
    endIcon: endIcon,
    loading: !!loading,
    style: _extends({
      pointerEvents: loading ? 'none' : 'auto'
    }, fullWidth ? {
      width: '100%'
    } : {}, style(_extends({
      pl: startIcon ? 2.5 : 3,
      pr: endIcon ? 2.5 : 3,
      '--focus-offset': '1px'
    }, props.sx))),
    "$tone": tone,
    showAccessory: !!loading,
    accessory: React__default.createElement(Box, {
      position: "absolute",
      left: "0",
      top: "0",
      bottom: "0",
      right: "0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }, React__default.createElement(Spinner, {
      size: "lg",
      color: "crunchberry-light"
    }))
  }, props), children);
};

var Star = function Star(props) {
  return React__default.createElement(SvgIcon, Object.assign({
    htmlColor: grape.medium
  }, props), React__default.createElement("path", {
    d: "M12.0015 17.27L16.6597 20.0815C17.2277 20.4243 17.9284 19.9151 17.7777 19.269L16.5415 13.97L20.6579 10.4039C21.1594 9.96951 20.8913 9.14581 20.2302 9.08973L14.8115 8.63L12.692 3.62928C12.4336 3.01951 11.5694 3.01951 11.3109 3.62928L9.19146 8.63L3.77269 9.08973C3.11162 9.14581 2.84356 9.96951 3.34501 10.4039L7.46147 13.97L6.22528 19.269C6.07455 19.9151 6.77523 20.4243 7.34321 20.0815L12.0015 17.27Z"
  }));
};

var _excluded$4 = ["size"];
var StyledStarRatingHolder = /*#__PURE__*/styled.div.withConfig({
  displayName: "StarRating__StyledStarRatingHolder",
  componentId: "sc-itki0c-0"
})(["line-height:0;& .MuiRating-root{font-size:28px;}& .MuiRating-sizeSmall{font-size:24px;}& .MuiRating-sizeLarge{font-size:32px;}"]);

var StarRating = function StarRating(_ref) {
  var _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'small' : _ref$size,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$4);

  return React__default.createElement(StyledStarRatingHolder, null, React__default.createElement(Rating, Object.assign({
    emptyIcon: React__default.createElement(Star, {
      htmlColor: gray['200'],
      fontSize: "inherit"
    }),
    icon: React__default.createElement(Star, {
      fontSize: "inherit"
    }),
    size: size,
    precision: 0.1
  }, props)));
};

function SvgAlert(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M8 11a.25.25 0 100 .501A.25.25 0 008 11z",
    fill: "currentColor",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), createElement("path", {
    d: "M8 9V3.5",
    stroke: "currentColor",
    strokeMiterlimit: 10,
    strokeLinecap: "round"
  }), createElement("path", {
    d: "M.5 8a7.5 7.5 0 1015 0 7.5 7.5 0 00-15 0v0z",
    stroke: "currentColor",
    strokeMiterlimit: 10
  }));
}

function SvgBell(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M7.06 4.634A6.048 6.048 0 004.71 6.988M6.262 2A6.19 6.19 0 002 6.263M4.857 17.714h14.286M19.143 17.714S17.714 16.471 17.714 12c0-3.571-2.44-6.731-5.714-6.731-3.274 0-5.714 3.16-5.714 6.731 0 4.494-1.429 5.714-1.429 5.714M13.429 20.571a1.428 1.428 0 11-2.857 0M12 5.269v-1.84M16.939 4.634a6.083 6.083 0 012.352 2.353M17.738 2A6.19 6.19 0 0122 6.263",
    stroke: "currentColor",
    strokeWidth: 1.25,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

function SvgClose(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M3.382 2.18l.081.071L8 6.787l4.537-4.536a.857.857 0 011.283 1.131l-.071.081L9.212 8l4.537 4.537a.857.857 0 01-1.132 1.283l-.08-.071L8 9.212l-4.537 4.537a.857.857 0 01-1.283-1.132l.071-.08L6.787 8 2.251 3.463A.857.857 0 013.295 2.12l.087.06z",
    fill: "currentColor"
  }));
}

function SvgInfo(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M9.5 11H9a1 1 0 01-1-1V7.5a.5.5 0 00-.5-.5H7",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), createElement("path", {
    d: "M7.75 4.5a.25.25 0 100 .5.25.25 0 000-.5z",
    fill: "currentColor",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), createElement("path", {
    d: "M.5 8a7.5 7.5 0 1015 0 7.5 7.5 0 00-15 0v0z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

function SvgWarning(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M8 12.5a.25.25 0 100 .501.25.25 0 000-.501z",
    fill: "currentColor",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), createElement("path", {
    d: "M8 10.5v-5",
    stroke: "currentColor",
    strokeMiterlimit: 10,
    strokeLinecap: "round"
  }), createElement("path", {
    d: "M9.08 1.173a1.204 1.204 0 00-2.16 0L.603 14.04a1.014 1.014 0 00.91 1.46h12.971a1.013 1.013 0 00.911-1.46L9.081 1.173z",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

var tones$1 = {
  warning: {
    text: 'peach-dark',
    background: 'peach-light',
    muted: 'peach'
  },
  critical: {
    text: 'cherry-dark',
    background: 'cherry-light',
    muted: 'cherry'
  },
  info: {
    text: 'gray-600',
    background: 'karl',
    muted: 'gray-500'
  },
  promote: {
    text: 'grape-dark',
    background: 'grape-xlight',
    muted: 'grape'
  }
};

var _excluded$5 = ["children", "type"];

var AlertAction = function AlertAction(_ref) {
  var children = _ref.children,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'button' : _ref$type,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);

  return React__default.createElement(BaseClickable$1, Object.assign({
    type: type,
    py: 1
  }, rest), React__default.createElement(Text, {
    variant: "body2",
    style: {
      textDecoration: 'underline',
      color: 'var(--action-text-color)'
    }
  }, children));
};

var alertVariants = {
  inline: {
    borderRadius: 'base'
  },
  banner: {
    borderRadius: 'none'
  }
};
var AlertContainer = /*#__PURE__*/styled(Stack).withConfig({
  displayName: "Alert__AlertContainer",
  componentId: "sc-k8nk99-0"
})( /*#__PURE__*/variant({
  variants: alertVariants
}));
var AnimatedAlertContainer = /*#__PURE__*/motion(AlertContainer);
/**
 * A screen-reader friendly alert component. Meant to be rendered at the top of a page.
 * Fills full width on mobile, and is rounded on larger screens.
 *
 */

var Alert = function Alert(_ref) {
  var _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'inline' : _ref$variant,
      _ref$tone = _ref.tone,
      tone = _ref$tone === void 0 ? 'warning' : _ref$tone,
      _ref$assertive = _ref.assertive,
      assertive = _ref$assertive === void 0 ? false : _ref$assertive,
      actions = _ref.actions,
      title = _ref.title,
      icon = _ref.icon,
      collapsible = _ref.collapsible,
      _ref$show = _ref.show,
      show = _ref$show === void 0 ? false : _ref$show,
      _ref$maxContentWidth = _ref.maxContentWidth,
      maxContentWidth = _ref$maxContentWidth === void 0 ? SolvTheme.breakpoints.sm : _ref$maxContentWidth,
      children = _ref.children;

  // This will get mutated internally
  var _useState = useState(show),
      visible = _useState[0],
      setVisible = _useState[1]; // But to preserve animations, this will not
  // eslint-disable-next-line @typescript-eslint/no-unused-vars


  var _useState2 = useState(show),
      initial = _useState2[0]; // Any time any prop changes, reset the visiblity values


  useEffect(function () {
    setVisible(show);
  }, [variant, tone, assertive, actions, show, title, icon, collapsible, maxContentWidth, children]);
  var tonePalette = tones$1[tone];
  var Icon = typeof icon !== 'undefined' ? icon : {
    warning: React__default.createElement(SvgWarning, null),
    critical: React__default.createElement(SvgAlert, null),
    info: React__default.createElement(SvgInfo, null),
    promote: React__default.createElement(SvgBell, null)
  }[tone];
  return React__default.createElement(AnimatePresence, null, visible && React__default.createElement(ReachAlert, {
    type: assertive ? 'assertive' : 'polite'
  }, React__default.createElement(AnimatedAlertContainer, {
    variant: variant,
    textColor: tonePalette.text,
    sx: {
      '--focusable-offset-color': SolvTheme.colors[tonePalette.background]
    },
    alignItems: "center",
    space: 2,
    row: true,
    bg: tonePalette.background,
    p: 2,
    fontSize: "xl",
    justifyContent: "center",
    width: "100%",
    initial: initial ? 'open' : 'collapsed',
    animate: "open",
    exit: "collapsed",
    variants: {
      open: {
        opacity: 1,
        height: 'auto'
      },
      collapsed: {
        opacity: 0,
        height: 0,
        margin: 0
      }
    },
    transition: {
      duration: 0.8,
      ease: [0.04, 0.62, 0.23, 0.98]
    }
  }, React__default.createElement(Stack, {
    alignItems: "flex-start",
    row: true,
    width: "100%",
    textColor: tonePalette.muted,
    maxWidth: variant === 'banner' ? maxContentWidth : undefined
  }, React__default.createElement(Stack, {
    space: 2,
    alignItems: "flex-start",
    row: true,
    width: "100%"
  }, !!Icon && React__default.createElement(Box, {
    flex: 0
  }, Icon), React__default.createElement(Stack, {
    alignSelf: "center",
    alignItems: "flex-start",
    flex: 1
  }, React__default.createElement(Text, {
    as: 'div',
    variant: "h4",
    textColor: tonePalette.text
  }, title), React__default.createElement(Text, {
    as: 'div',
    textColor: tonePalette.text
  }, children), React__default.createElement(Stack, {
    row: true,
    space: 2,
    sx: {
      '--action-text-color': SolvTheme.colors[tonePalette.text]
    }
  }, actions))), collapsible && variant !== 'banner' && React__default.createElement(BaseClickable$1, {
    onClick: function onClick() {
      return setVisible(false);
    },
    fontSize: "sm",
    textColor: "gray-500"
  }, React__default.createElement(SvgClose, null))))));
};

Alert.Action = AlertAction;

var _excluded$6 = ["children", "subtle", "size"];
var linkSizes = {
  base: /*#__PURE__*/style(typography.body),
  small: /*#__PURE__*/style(typography.body2)
};
var linkStyles = {
  primary: {
    color: 'crunchberry',
    ':hover': {
      color: 'crunchberry-medium'
    },
    ':disabled, .disabled': {
      color: 'crunchberry-light',
      pointerEvents: 'none'
    },
    ':active': {
      color: 'crunchberry-dark'
    },
    ':visited': {
      color: 'crunchberry'
    }
  },
  subtle: {
    color: 'gray-600',
    ':hover, :active': {
      textDecoration: 'underline'
    },
    ':disabled, .disabled': {
      color: 'gray-300',
      pointerEvents: 'none'
    },
    ':visited': {
      color: 'gray-600'
    }
  }
};
var LinkBase = /*#__PURE__*/styled(BaseClickable$1).withConfig({
  displayName: "Link__LinkBase",
  componentId: "sc-16k1qrm-0"
})( /*#__PURE__*/variant({
  prop: 'size',
  variants: linkSizes
}), /*#__PURE__*/variant({
  variants: linkStyles
}), solvSystem, sx);

var Link = function Link(_ref) {
  var children = _ref.children,
      subtle = _ref.subtle,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'base' : _ref$size,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$6);

  return React__default.createElement(LinkBase, Object.assign({
    variant: subtle ? 'subtle' : 'primary',
    textColor: subtle ? 'gray-600' : 'crunchberry',
    size: size,
    sx: {
      '--focus-offset': '6px'
    }
  }, props), children);
};

function SvgExclamationSmall(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("circle", {
    cx: 8,
    cy: 8,
    r: 5.5,
    stroke: "#CE2424"
  }), createElement("path", {
    stroke: "#CE2424",
    strokeWidth: 1.5,
    strokeLinecap: "round",
    d: "M8 8.375v-3"
  }), createElement("circle", {
    r: 0.75,
    transform: "matrix(1 0 0 -1 8 10.625)",
    fill: "#CE2424"
  }));
}

var FormControl = function FormControl(_ref) {
  var children = _ref.children,
      title = _ref.title,
      disabled = _ref.disabled,
      error = _ref.error,
      helperText = _ref.helperText,
      optional = _ref.optional,
      _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'label' : _ref$as;
  return React__default.createElement(Stack, {
    as: as,
    space: 1
  }, title && React__default.createElement(Text, {
    variant: "subtitle2",
    textColor: disabled ? 'gray-300' : error ? 'cherry' : 'gray-600'
  }, title, optional && React__default.createElement(Text, {
    as: "span",
    variant: "body2",
    ml: 1,
    muted: true
  }, typeof optional === 'string' ? optional : 'Optional')), children, helperText && !error && React__default.createElement(Text, {
    variant: "body2",
    muted: true
  }, helperText), error && typeof error === 'string' && error.trim() !== '' && React__default.createElement(Stack, {
    row: true,
    space: 0.5,
    fontSize: "base",
    alignItems: "flex-start",
    textColor: "cherry"
  }, React__default.createElement(Box, {
    mt: "2px"
  }, React__default.createElement(SvgExclamationSmall, null)), React__default.createElement(Text, {
    variant: "body2",
    textColor: "cherry"
  }, error)));
};

var StyledBase = /*#__PURE__*/styled(Stack).withConfig({
  displayName: "InputBase__StyledBase",
  componentId: "sc-1wi64h8-0"
})(["&:focus-within{box-shadow:0 0 0 1px black;border-color:", ";}&:hover:not(:has(:disabled)){border-color:", ";}"], palette.gray[600], function (props) {
  return !!props.$error ? palette.cherry.dark : palette.gray[600];
});
var InputBase = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      dense = _ref.dense,
      startIcon = _ref.startIcon,
      endIcon = _ref.endIcon,
      disabled = _ref.disabled,
      cursor = _ref.cursor,
      error = _ref.error,
      noPadding = _ref.noPadding,
      variableHeight = _ref.variableHeight;
  return React__default.createElement(StyledBase, {
    border: "solid 1px",
    borderColor: disabled ? 'gray-300' : error ? 'cherry' : 'gray-400',
    borderRadius: "base",
    className: "focusable",
    px: noPadding ? 0 : 2,
    height: variableHeight ? undefined : SolvTheme.space[dense ? 6 : 7],
    alignItems: "center",
    "$error": error,
    "$disabled": disabled,
    sx: {
      cursor: disabled ? 'default' : cursor,
      '--focus-offset': 0,
      '--focus-width': 2
    },
    ref: ref,
    row: true
  }, startIcon != null && startIcon, React__default.createElement(Box, {
    flex: "1",
    justifyContent: "stretch",
    display: "flex",
    alignItems: "stretch",
    alignSelf: "stretch"
  }, children), endIcon != null && endIcon);
});

var _excluded$7 = ["title", "disabled", "error", "helperText", "optional", "dense", "startIcon", "endIcon", "inputComponent", "containerRef"];

var _templateObject;
var StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "TextField__StyledInput",
  componentId: "sc-dla8xb-0"
})({}, /*#__PURE__*/css(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    background: white;\n    appearance: none;\n    --webkit-appearance: none;\n    border: none;\n    display: block;\n    width: 100%;\n    align-self: stretch;\n    height: 100%;\n    display: flex;\n    align-items: center;\n    justify-content: flex-start;\n    padding: 0;\n\n    &:focus {\n      outline: none;\n    }\n\n    &:disabled {\n      background: none;\n    }\n  "]))), /*#__PURE__*/style(typography.body), function (_ref) {
  var $dense = _ref.$dense;
  return style({
    px: 2,
    py: $dense ? 1.5 : 2,
    borderRadius: 'base'
  });
});
var TextField = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var title = _ref2.title,
      disabled = _ref2.disabled,
      error = _ref2.error,
      helperText = _ref2.helperText,
      optional = _ref2.optional,
      dense = _ref2.dense,
      startIcon = _ref2.startIcon,
      endIcon = _ref2.endIcon,
      _ref2$inputComponent = _ref2.inputComponent,
      inputComponent = _ref2$inputComponent === void 0 ? 'input' : _ref2$inputComponent,
      containerRef = _ref2.containerRef,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded$7);

  return React__default.createElement(FormControl, Object.assign({}, {
    title: title,
    disabled: disabled,
    error: error,
    helperText: helperText,
    optional: optional
  }), React__default.createElement(InputBase, Object.assign({
    noPadding: true,
    cursor: "text"
  }, {
    dense: dense,
    startIcon: startIcon,
    endIcon: endIcon,
    disabled: disabled,
    error: error
  }, {
    ref: containerRef
  }), React__default.createElement(StyledInput, Object.assign({
    as: inputComponent
  }, props, {
    "$dense": dense,
    disabled: disabled,
    ref: ref
  }))));
});

var _excluded$8 = ["title", "disabled", "error", "helperText", "optional", "dense", "startIcon", "endIcon", "inputComponent"];

var _templateObject$1;
var StyledInput$1 = /*#__PURE__*/styled.textarea.withConfig({
  displayName: "TextArea__StyledInput",
  componentId: "sc-xgbbfw-0"
})({}, /*#__PURE__*/css(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    background: white;\n    appearance: none;\n    --webkit-appearance: none;\n    border: none;\n    display: block;\n    width: 100%;\n    align-self: stretch;\n    height: 100%;\n    display: flex;\n    align-items: center;\n    justify-content: flex-start;\n    padding: 0;\n\n    &:focus {\n      outline: none;\n    }\n\n    &:disabled {\n      background: none;\n    }\n  "]))), /*#__PURE__*/style(typography.body), function (_ref) {
  var $dense = _ref.$dense;
  return style({
    px: 2,
    py: $dense ? 1.5 : 2,
    borderRadius: 'base'
  });
});

var TextArea = function TextArea(_ref2) {
  var title = _ref2.title,
      disabled = _ref2.disabled,
      error = _ref2.error,
      helperText = _ref2.helperText,
      optional = _ref2.optional,
      dense = _ref2.dense,
      startIcon = _ref2.startIcon,
      endIcon = _ref2.endIcon,
      _ref2$inputComponent = _ref2.inputComponent,
      inputComponent = _ref2$inputComponent === void 0 ? 'textarea' : _ref2$inputComponent,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded$8);

  return React__default.createElement(FormControl, Object.assign({}, {
    title: title,
    disabled: disabled,
    error: error,
    helperText: helperText,
    optional: optional
  }), React__default.createElement(InputBase, Object.assign({
    variableHeight: true,
    noPadding: true,
    cursor: "text"
  }, {
    dense: dense,
    startIcon: startIcon,
    endIcon: endIcon,
    disabled: disabled,
    error: error
  }), React__default.createElement(StyledInput$1, Object.assign({
    as: inputComponent
  }, props, {
    "$dense": dense,
    disabled: disabled
  }))));
};

var _excluded$9 = ["title", "children", "disabled", "error", "helperText", "optional", "dense", "startIcon"];

var _templateObject$2;
var StyledSelect = /*#__PURE__*/styled.select.withConfig({
  displayName: "Select__StyledSelect",
  componentId: "sc-1843mwe-0"
})({
  backgroundPosition: "right " + SolvTheme.space[2] + " center"
}, /*#__PURE__*/css(_templateObject$2 || (_templateObject$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    background-color: white;\n    background-image: url(\"data:image/svg+xml, %3Csvg width='10' height='7' viewBox='0 0 10 7' fill='none' xmlns='http://www.w3.org/2000/svg' %3E%3Cpath d='M9.24264 1.24264L5 5.48528L0.757359 1.24264' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' /%3E%3C/svg%3E\");\n    background-repeat: no-repeat;\n    color-adjust: exact;\n    border: none;\n    margin: 0;\n    font-family: inherit;\n    font-size: inherit;\n    cursor: inherit;\n    line-height: inherit;\n\n    border: none;\n    display: block;\n    width: 100%;\n    appearance: none;\n\n    &:focus {\n      outline: none;\n    }\n\n    &:disabled {\n      background: none;\n    }\n  "]))), /*#__PURE__*/style(typography.body), function (_ref) {
  var $dense = _ref.$dense;
  return style({
    px: 2,
    py: $dense ? 1.5 : 2,
    borderRadius: 'base'
  });
});
var Select = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var title = _ref2.title,
      children = _ref2.children,
      disabled = _ref2.disabled,
      error = _ref2.error,
      helperText = _ref2.helperText,
      optional = _ref2.optional,
      dense = _ref2.dense,
      startIcon = _ref2.startIcon,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded$9);

  return React__default.createElement(FormControl, Object.assign({}, {
    title: title,
    disabled: disabled,
    error: error,
    helperText: helperText,
    optional: optional
  }), React__default.createElement(InputBase, Object.assign({
    noPadding: true,
    cursor: "pointer"
  }, {
    dense: dense,
    startIcon: startIcon,
    disabled: disabled,
    error: error
  }), React__default.createElement(StyledSelect, Object.assign({}, props, {
    "$dense": dense,
    disabled: disabled,
    ref: ref
  }), children)));
});

function SvgChevronLeft(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 9 14",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M7.343 1.343L1.686 7l5.657 5.657",
    stroke: "#414F60",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

var _excluded$a = ["children"];
var IconButtonBase = /*#__PURE__*/styled(BaseClickable$1).withConfig({
  displayName: "IconButton__IconButtonBase",
  componentId: "sc-jieymj-0"
})( /*#__PURE__*/style({
  '&&': {
    p: 2,
    borderRadius: 'full',
    transition: 'background .15s',
    ':hover': {
      bg: 'gray-100'
    },
    ':active': {
      bg: 'gray-200'
    }
  }
}), solvSystem, sx);

var IconButton = function IconButton(_ref) {
  var children = _ref.children,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$a);

  return React__default.createElement(IconButtonBase, Object.assign({}, props), children);
};

var NavBar = function NavBar(_ref) {
  var children = _ref.children,
      hideBackButton = _ref.hideBackButton,
      onGoBack = _ref.onGoBack,
      progress = _ref.progress;

  var _useJigsaw = useJigsaw(),
      history = _useJigsaw.history;

  var showProgress = typeof progress !== 'undefined';
  return React__default.createElement(Stack, null, React__default.createElement(Box, {
    height: "64px",
    alignItems: "center",
    alignSelf: "stretch",
    boxShadow: showProgress ? 'none' : 'sm',
    justifyContent: "center",
    px: 2,
    py: 1,
    display: "grid",
    gridTemplateColumns: "1fr 3fr 1fr"
  }, React__default.createElement(Box, {
    display: "flex",
    flex: "1",
    justifyContent: "flex-start"
  }, !hideBackButton && React__default.createElement(IconButton, {
    onClick: onGoBack != null ? onGoBack : history.goBack
  }, React__default.createElement(SvgChevronLeft, null))), React__default.createElement(Box, {
    display: "flex",
    flex: "1",
    justifyContent: "center"
  }, typeof children === 'string' ? React__default.createElement(Text, {
    variant: "subtitle2"
  }, children) : children), React__default.createElement(Box, {
    flex: "1"
  })), typeof progress !== 'undefined' && React__default.createElement(Box, {
    height: "4px",
    alignSelf: "stretch",
    bg: "gray-200"
  }, React__default.createElement(Box, {
    height: "4px",
    width: progress * 100 + "%",
    bg: "crunchberry",
    sx: {
      transition: 'width 75ms ease-in-out'
    }
  })));
};

var _excluded$b = ["title", "subtitle"];

var PageHeader = function PageHeader(_ref) {
  var title = _ref.title,
      subtitle = _ref.subtitle,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$b);

  return React__default.createElement(Stack, Object.assign({
    borderColor: "gray-100",
    space: 1
  }, rest), title && React__default.createElement(Text, {
    variant: "h3"
  }, title), subtitle && React__default.createElement(Text, {
    muted: true
  }, subtitle));
};

var Table = function Table(_ref) {
  var children = _ref.children;
  return React__default.createElement(Stack, {
    bg: "gray-100",
    space: 1
  }, children);
};

var _excluded$c = ["children", "title", "accessory"];

var TableSection = function TableSection(_ref) {
  var children = _ref.children,
      title = _ref.title,
      accessory = _ref.accessory,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$c);

  return React__default.createElement(Stack, Object.assign({
    bg: "white",
    px: 3,
    py: 3
  }, rest), (title || accessory) && React__default.createElement(Stack, {
    pb: 1,
    row: true,
    justifyContent: "space-between"
  }, title && (typeof title === 'string' ? React__default.createElement(Text, {
    variant: "h4"
  }, title) : title), accessory && accessory), children);
};

var TableRow = function TableRow(_ref) {
  var title = _ref.title,
      value = _ref.value,
      suppressValue = _ref.suppressValue;
  return React__default.createElement(Stack, {
    row: true,
    justifyContent: "space-between",
    space: 2,
    py: 2,
    borderBottom: "solid 1px",
    borderColor: "gray-200",
    sx: {
      ':last-child': {
        borderWidth: 0,
        pb: 1
      }
    }
  }, typeof title === 'string' ? React__default.createElement(Text, {
    muted: true
  }, title) : title, typeof value === 'string' ? React__default.createElement(Text, Object.assign({}, suppressValue ? {
    'data-hj-suppress': true
  } : {}), value) : value);
};

function SvgChevronRight(props) {
  return createElement("svg", Object.assign({
    width: "1em",
    height: "1em",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, props), createElement("path", {
    d: "M11.657 6.343L17.314 12l-5.657 5.657",
    stroke: "#414F60",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

var _excluded$d = ["title", "subtitle", "icon", "iconUrl"];
var Avatar = /*#__PURE__*/styled.img.withConfig({
  displayName: "ChoiceCard__Avatar",
  componentId: "sc-1lbr75n-0"
})( /*#__PURE__*/style({
  width: space[6],
  height: space[6],
  borderRadius: 'full',
  display: 'block',
  flex: 0
}));
var StyledChoiceCard = /*#__PURE__*/styled(BaseClickable$1).withConfig({
  displayName: "ChoiceCard__StyledChoiceCard",
  componentId: "sc-1lbr75n-1"
})(function (_ref) {
  var row = _ref.row;
  return style(_extends({}, row ? {
    borderBottom: 'solid 1px',
    borderRadius: 'none'
  } : {
    border: 'solid 1px',
    borderRadius: 'base'
  }, {
    '--focus-offset': '0px',
    backgroundColor: 'white',
    borderColor: 'gray-200',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    p: 2,
    ':disabled': {
      pointerEvents: 'none',
      opacity: 0.5
    },
    ':hover': {
      borderColor: 'gray-300'
    },
    ':active': {
      borderColor: 'gray-500'
    }
  }));
});
/**
 * A full width, low-visual-weight button with a disclosure indicator used in multi-step forms.
 *
 */

var ChoiceCard = function ChoiceCard(_ref2) {
  var title = _ref2.title,
      subtitle = _ref2.subtitle,
      icon = _ref2.icon,
      iconUrl = _ref2.iconUrl,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded$d);

  return React__default.createElement(StyledChoiceCard, Object.assign({
    contentProps: {
      flex: 1
    }
  }, rest), React__default.createElement(Stack, {
    row: true,
    width: "100%",
    flex: "1",
    justifyContent: "space-between",
    alignItems: "center"
  }, React__default.createElement(Stack, {
    alignItems: "center",
    row: true,
    space: 2,
    textColor: "gray-500"
  }, function () {
    if (iconUrl) return React__default.createElement(Avatar, {
      width: space[6],
      height: space[6],
      src: iconUrl,
      alt: "Icon"
    });
    if (icon) return icon;
    return null;
  }(), React__default.createElement(Stack, {
    alignItems: "flex-start"
  }, typeof title === 'string' ? React__default.createElement(Text, {
    variant: "subtitle"
  }, title) : title, subtitle && (typeof subtitle === 'string' ? React__default.createElement(Text, {
    muted: true
  }, subtitle) : subtitle))), React__default.createElement(Box, null, React__default.createElement(SvgChevronRight, null))));
};

/**
 * A context to manage the state of a togglable form component.
 */

var ToggleContext = /*#__PURE__*/createContext(undefined);
/**
 * A provider for the toggle context.
 */

var ToggleProvider = function ToggleProvider(_ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      dense = _ref.dense;
  var value = {
    state: {
      disabled: disabled,
      dense: dense
    }
  };
  return React__default.createElement(ToggleContext.Provider, {
    value: value
  }, children);
};
/**
 * Access the current toggle context.
 */

function useToggle() {
  var context = useContext(ToggleContext);

  if (context === undefined) {
    throw new Error('useToggle must be used within a ToggleProvider');
  }

  return context.state;
}

var _excluded$e = ["value", "disabled"];
var Base = /*#__PURE__*/styled(BaseClickable$1).withConfig({
  displayName: "ToggleButton__Base",
  componentId: "sc-1pxt5n0-0"
})( /*#__PURE__*/style( /*#__PURE__*/_extends({
  transition: 'all 75ms',
  bg: 'egg',
  overflow: 'hidden'
}, typography.body, {
  border: 'solid 1px',
  borderColor: 'gray-400',
  borderRadius: 'base',
  ':hover': {
    bg: 'gray-100'
  },
  ':active': {
    borderColor: 'gray-600',
    color: 'gray-600'
  },
  '&.disabled': {
    borderColor: 'gray-300',
    color: 'gray-400'
  },
  ':focus': {
    outline: 'none'
  },
  '&.active, &.active.checked': {
    borderColor: 'gray-600',
    boxShadow: 'focus',
    '--focus-offset': '0px',
    '--focus-width': '1px'
  },
  '&.checked': {
    bg: 'gray-200',
    borderColor: 'gray-600',
    '&:hover': {
      bg: 'gray-200'
    },
    '&:active': {
      bg: 'gray-100'
    }
  }
})));

var ToggleButton = function ToggleButton(_ref) {
  var value = _ref.value,
      forceDisabled = _ref.disabled,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$e);

  var _useToggle = useToggle(),
      dense = _useToggle.dense,
      stateDisabled = _useToggle.disabled;

  var disabled = forceDisabled || stateDisabled;
  return React__default.createElement(RadioGroup.Option, Object.assign({
    className: function className(_ref2) {
      var active = _ref2.active,
          checked = _ref2.checked;
      return classnames({
        active: active,
        checked: checked,
        disabled: disabled
      });
    },
    as: Base,
    value: value,
    disabled: disabled,
    disableDefaultFocusStyles: true,
    type: "button"
  }, props), function (_ref3) {
    var checked = _ref3.checked,
        active = _ref3.active;
    return React__default.createElement(Stack, Object.assign({
      className: classnames({
        selected: checked,
        active: active
      }),
      sx: {
        px: dense ? 2 : 3,
        py: dense ? 1.5 : 2
      }
    }, props));
  });
};

var _excluded$f = ["children", "title"];

var InnerToggleButtonGroup = function InnerToggleButtonGroup(_ref) {
  var children = _ref.children,
      title = _ref.title,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$f);

  var _useToggle = useToggle(),
      disabled = _useToggle.disabled;

  return React__default.createElement(FormControl, Object.assign({
    title: title && React__default.createElement(RadioGroup.Label, null, title),
    as: "div",
    disabled: disabled
  }, props), React__default.createElement(Stack, {
    row: true,
    space: 1
  }, children));
};

var _excluded$g = ["disabled", "dense", "value", "onChange"];

var ToggleButtonGroup = function ToggleButtonGroup(_ref) {
  var disabled = _ref.disabled,
      dense = _ref.dense,
      value = _ref.value,
      onChange = _ref.onChange,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$g);

  return React__default.createElement(ToggleProvider, {
    disabled: disabled,
    dense: dense
  }, React__default.createElement(RadioGroup, {
    value: value,
    onChange: onChange
  }, React__default.createElement(InnerToggleButtonGroup, Object.assign({}, props))));
};

var CssReset = /*#__PURE__*/createGlobalStyle(["*,::after,::before{box-sizing:border-box}html{line-height:1.15;-webkit-text-size-adjust:100%;-moz-tab-size:4;tab-size:4}body{margin:0;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif,'Apple Color Emoji','Segoe UI Emoji'}hr{height:0;color:inherit}abbr[title]{text-decoration:underline dotted}b,strong{font-weight:bolder}code,kbd,pre,samp{font-family:ui-monospace,SFMono-Regular,Consolas,'Liberation Mono',Menlo,monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;line-height:1.15;margin:0}button,select{text-transform:none}[type=button],[type=reset],[type=submit],button{-webkit-appearance:button}::-moz-focus-inner{border-style:none;padding:0}:-moz-focusring{outline:1px dotted ButtonText}:-moz-ui-invalid{box-shadow:none}legend{padding:0}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}"]);

export { Alert, BaseClickable$1 as BaseClickable, Box, Button, ChoiceCard, CssReset, FormControl, GlobalStyles, JigsawContext, JigsawProvider, Link, NavBar, PageHeader, Select, SolvTheme, SolvThemeProvider, Spinner, Stack, StarRating, Table, TableRow, TableSection, Text, TextArea, TextField, ToggleButton, ToggleButtonGroup, colors$1 as palette, style, typography, useJigsaw };
//# sourceMappingURL=jigsaw.esm.js.map
